import React from "react";
import { compose } from "redux";
import { connect } from "react-redux";
import { createStructuredSelector } from "reselect";
import { useTranslation } from "react-i18next";
import Drawer from "@mui/material/Drawer";
import { Flex } from "../../../../components/Container/Flex";
import { IconButton } from "../../../../components/IconButton";
import Button from "../../../../components/Controls/Button";
import { ArrowLeftIcon, CloseIcon, PlusIcon } from "../../../../utils/icons";
import { Secondary, Text } from "../../../../utils/style/texts";
import { Colors } from "../../../../utils/style/colors";
import * as styles from "../style";
import { getAppTiles } from "../tiles";
import AppTile from "../components/AppTile";
import configs from "../Configs";
import { selectExampleTile } from "../selectors";

function mapDispatchToProps() {
  return {};
}

const mapStateToProps = createStructuredSelector({
  exampleTile: selectExampleTile(),
});

export default compose(connect(mapStateToProps, mapDispatchToProps))(
  ({ isOpen, onClose, onSaveTile, editTile, exampleTile }) => {
    const { t } = useTranslation();
    // Recomputed on language change; AppTile re-resolves its own per render.
    const tiles = React.useMemo(() => getAppTiles(t), [t]);
    const [configTile, setConfigTile] = React.useState(null);

    // When editing an existing tile, jump straight to its config form.
    React.useEffect(() => {
      if (!configTile && editTile) {
        setConfigTile(tiles.find((x) => x.type === editTile.type));
      }
    }, [editTile, configTile, tiles]);

    // Reset to the library when (re)opening for a brand new tile.
    React.useEffect(() => {
      if (isOpen && !editTile) setConfigTile(null);
    }, [isOpen, editTile]);

    const ConfigComponent = configTile ? configs[configTile.config] : null;

    return (
      <Drawer
        open={isOpen}
        onClose={onClose}
        anchor="right"
        // Same layer as the dialog it opens over, not above it: MUI portals stack
        // by open order, and going higher buries the pickers this drawer opens.
        sx={{ zIndex: (theme) => theme.zIndex.modal }}
        PaperProps={{
          style: {
            backgroundColor: Colors.gray10,
            minWidth: "460px",
            width: "34vw",
            padding: 0,
          },
        }}
      >
        <Flex col style={{ height: "100vh" }}>
          {/* Preview header only while editing a tile — hidden in the library list */}
          {configTile && (
            <styles.ExampleArea>
              {exampleTile && (
                <styles.PreviewTile>
                  <AppTile
                    type={exampleTile.type}
                    config={exampleTile.config}
                  />
                </styles.PreviewTile>
              )}
            </styles.ExampleArea>
          )}

          <styles.DrawerHeader row>
            {configTile && !editTile ? (
              <IconButton onClick={() => setConfigTile(null)} size="large">
                <ArrowLeftIcon />
              </IconButton>
            ) : (
              <IconButton
                onClick={() => {
                  setConfigTile(null);
                  onClose();
                }}
                size="large"
              >
                <CloseIcon />
              </IconButton>
            )}
            <Secondary>
              {t("appPageLibrary", { defaultValue: "Tile library" })}
            </Secondary>
            {configTile && (
              <>
                <Secondary>&nbsp;/&nbsp;</Secondary>
                <Secondary>{configTile.title}</Secondary>
              </>
            )}
          </styles.DrawerHeader>

          <styles.DrawerBody col>
            {ConfigComponent ? (
              <ConfigComponent
                tile={configTile}
                editConfig={editTile?.config}
                onCancel={() => {
                  if (editTile) onClose();
                  setConfigTile(null);
                }}
                onAccept={(config) => {
                  onSaveTile(configTile, config);
                  setConfigTile(null);
                }}
              />
            ) : (
              tiles.map((tile) => (
                <styles.LibraryCard key={tile.type} row align="center">
                  <styles.PreviewTile style={{ width: 90, height: 90 }}>
                    <AppTile type={tile.type} config={{}} />
                  </styles.PreviewTile>
                  <Flex col style={{ marginLeft: 16, flex: 1 }}>
                    <Text>{tile.title}</Text>
                    <Secondary>{tile.description}</Secondary>
                    <div>
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<PlusIcon />}
                        onClick={() => setConfigTile(tile)}
                        style={{ marginTop: 10 }}
                      >
                        {t("add")}
                      </Button>
                    </div>
                  </Flex>
                </styles.LibraryCard>
              ))
            )}
          </styles.DrawerBody>
        </Flex>
      </Drawer>
    );
  }
);
